/**
 * Issue #57 — le signal global de session perdue, et sa porte de sortie.
 *
 * ## Le problème que ce module résout, et celui qu'il refuse de rouvrir
 *
 * Quand la session tombe, **toutes** les requêtes en cours échouent en même temps. Chaque page
 * affichait alors sa propre bannière rouge dans son propre coin, avec le message technique de son
 * propre appel : l'utilisateur voyait trois symptômes d'un seul événement, et aucun ne lui disait
 * quoi faire. C'est le troisième symptôme relevé le 2026-08-29.
 *
 * La correction évidente — rediriger vers le login dès qu'un 401 tombe — est exactement ce que
 * l'issue **#43** a coûté : une boucle silencieuse entre le portail et Entra, sans écran, sans
 * message, sans sortie. Ce module ne redirige donc **jamais de lui-même**. Il lève un drapeau ; la
 * racine affiche une bannière ; et c'est **l'utilisateur** qui clique. Le geste reste explicite,
 * donc observable, donc interruptible.
 *
 * ## Pourquoi un module et non un contexte React
 *
 * L'émetteur est `apiClient`, qui n'est pas un composant et n'a aucun accès à l'arbre React. Un
 * état hors de React, lu par `useSyncExternalStore`, est la forme prévue pour exactement ce cas.
 * Le drapeau vit le temps de la page : une navigation dure le remet à zéro, ce qui est le
 * comportement voulu — rien à réarmer, rien à purger.
 */

let perdue = false
const abonnes = new Set<() => void>()

function notifier(): void {
  for (const abonne of abonnes) abonne()
}

/**
 * Déclaré par `apiClient` quand il renonce sans avoir lui-même lancé de reprise.
 *
 * Il ne le déclare **pas** quand il vient de démarrer une redirection : dans ce cas le navigateur
 * part chez Entra, et afficher une bannière « votre session a expiré » sur une page qu'on quitte
 * n'apprendrait rien à personne.
 */
export function signalerSessionPerdue(): void {
  if (perdue) return
  perdue = true
  notifier()
}

/** Oublié à la reprise explicite de l'utilisateur, pour que la bannière ne survive pas au geste. */
export function oublierSessionPerdue(): void {
  if (!perdue) return
  perdue = false
  notifier()
}

export function sessionEstPerdue(): boolean {
  return perdue
}

/** Contrat `useSyncExternalStore` : rend la fonction de désabonnement. */
export function surSessionPerdue(abonne: () => void): () => void {
  abonnes.add(abonne)
  return () => {
    abonnes.delete(abonne)
  }
}
